import {
  GroupCellRendererParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { PRICING_FORM_FIELDS } from 'utils/constants'
import { formatDate, getDepartment, getFamily, getSection } from 'utils/helpers'

const {
  BARCODE,
  PROMO,
  PROMO_END_DATE,
  PROMO_START_DATE,
  SPECIAL_PRICE,
  SPECIAL_PRICE_END_DATE,
  SPECIAL_PRICE_START_DATE,
  SLASH_PRICE,
  SLASH_PRICE_END_DATE,
  SLASH_PRICE_START_DATE,
  DEPARTMENT,
  ERP_BRAND,
  ERP_ITEM_DESC,
  ERP_PACK_INFO,
  ERP_PRICE,
  ERP_UOM,
  FAMILY,
  ITEM_NUMBER,
  ONLINE_DESC,
  ONLINE_PACK_INFO,
  AGGREGATOR_ID,
  QTY_PER_UOM,
  SECTION,
  STORE_ID,
  STORE_NAME,
  SUB_FAMILY,
  ONLINE_PRICE,
  BLOCK_SALE_ON_POS,
} = PRICING_FORM_FIELDS

export const columns = [
  {
    headerName: STORE_NAME.label,
    field: `store.${STORE_NAME.key}`,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: STORE_ID.label,
    field: 'store._id',
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: AGGREGATOR_ID.label,
    field: `aggregator._id`,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: BARCODE.label,
    field: `catalogue._id`,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: ERP_ITEM_DESC.label,
    field: `catalogue.${ERP_ITEM_DESC.key}`,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: ERP_PRICE.label,
    field: ERP_PRICE.key,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: ITEM_NUMBER.label,
    field: `catalogue.${ITEM_NUMBER.key}`,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: ERP_UOM.label,
    field: `catalogue.${ERP_UOM.key}`,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: QTY_PER_UOM.label,
    field: `catalogue.${QTY_PER_UOM.key}`,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: ERP_PACK_INFO.label,
    field: `catalogue.${ERP_PACK_INFO.key}`,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: SLASH_PRICE.label,
    field: SLASH_PRICE.key,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: SLASH_PRICE_START_DATE.label,
    field: SLASH_PRICE_START_DATE.key,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? formatDate(params.value) : '-',
  },
  {
    headerName: SLASH_PRICE_END_DATE.label,
    field: SLASH_PRICE_END_DATE.key,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? formatDate(params.value) : '-',
  },
  {
    headerName: SPECIAL_PRICE.label,
    field: SPECIAL_PRICE.key,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: SPECIAL_PRICE_START_DATE.label,
    field: SPECIAL_PRICE_START_DATE.key,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? formatDate(params.value) : '-',
  },
  {
    headerName: SPECIAL_PRICE_END_DATE.label,
    field: SPECIAL_PRICE_END_DATE.key,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? formatDate(params.value) : '-',
  },
  {
    headerName: DEPARTMENT.label,
    field: `catalogue.${DEPARTMENT.key}`,
    valueFormatter: (params: ValueFormatterParams) => {
      return getDepartment(params.value)
    },
  },
  {
    headerName: SECTION.label,
    field: `catalogue.${SECTION.key}`,
    valueFormatter: (params: ValueFormatterParams) => {
      return getSection(params.value)
    },
  },
  {
    headerName: FAMILY.label,
    field: `catalogue.${FAMILY.key}`,
    valueFormatter: (params: ValueFormatterParams) => {
      return getFamily(params.value)
    },
  },
  {
    headerName: SUB_FAMILY.label,
    field: `catalogue.${SUB_FAMILY.key}`,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: ERP_BRAND.label,
    field: `catalogue.${ERP_BRAND.key}`,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: ONLINE_DESC.label,
    field: `catalogue.${ONLINE_DESC.key}`,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: PROMO.label,
    field: PROMO.key,
    valueFormatter: (params: ValueFormatterParams) => {
      return params?.value === 1
        ? 'Active'
        : params?.value === 0
          ? 'Inactive'
          : '-'
    },
  },
  {
    headerName: PROMO_START_DATE.label,
    field: PROMO_START_DATE.key,
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value ? formatDate(params.value) : '-'
    },
  },
  {
    headerName: PROMO_END_DATE.label,
    field: PROMO_END_DATE.key,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? formatDate(params.value) : '-',
  },
  {
    headerName: ONLINE_PACK_INFO.label,
    field: `catalogue.${ONLINE_PACK_INFO.key}`,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: BLOCK_SALE_ON_POS.label,
    field: BLOCK_SALE_ON_POS.key,
    cellRenderer: (params: GroupCellRendererParams) =>
      params.value ? 'Yes' : 'No',
  },
]

// To flatten the pricing data added via the form
export const createFlatPricingData = (data: Record<string, any>) => {
  const flatData: Record<string, any> = {}
  // Special Price
  flatData[SPECIAL_PRICE.key] = data[SPECIAL_PRICE.key]

  // Special Price Start
  flatData[SPECIAL_PRICE_START_DATE.key] = data[SPECIAL_PRICE_START_DATE.key]

  // Special Price End
  flatData[SPECIAL_PRICE_END_DATE.key] = data[SPECIAL_PRICE_END_DATE.key]

  // Slash Price
  flatData[SLASH_PRICE.key] = data[SLASH_PRICE.key]

  // Slash Price Start
  flatData[SLASH_PRICE_START_DATE.key] = data[SLASH_PRICE_START_DATE.key]

  // Slash Price End
  flatData[SLASH_PRICE_END_DATE.key] = data[SLASH_PRICE_END_DATE.key]

  // Promo
  flatData[PROMO.key] = data[PROMO.key]

  // Promo Start Date
  flatData[PROMO_START_DATE.key] = data[PROMO_START_DATE.key]

  // Promo Start Date
  flatData[PROMO_END_DATE.key] = data[PROMO_END_DATE.key]

  // Online Price
  flatData[ONLINE_PRICE.key] = data[ONLINE_PRICE.key]

  return flatData
}
